import Bugsnag from '@bugsnag/js'
import { Box, CircularProgress, Divider, Icon, Typography } from '@mui/material'
import AdxCheckbox from 'components/custom/AdxCheckbox'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxActionButton from 'components/reusable/ActionButton/AdxActionButton'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import React, { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import {
  getEmployeeClaims,
  saveEmployeeClaims,
} from 'services/api/modules/payroll/payroll-claims'

const formatAmount = (amount) =>
  Number(amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// Pick the approved claims to pay with this employee's payslip. Saved on Apply, like overtime.
const ClaimsModal = ({ children, time_id, period, employee }) => {
  const [isModalOpen, setModalOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery(
    ['employeeClaims', time_id, employee?.id],
    async () => {
      const response = await getEmployeeClaims({ time_id, employee_id: employee?.id })
      return response?.data?.claims ?? []
    },
    { enabled: isModalOpen && !!employee?.id }
  )
  const claims = useMemo(() => data ?? [], [data])

  useEffect(() => {
    if (isModalOpen) {
      setSelectedIds(claims.filter((claim) => claim.selected).map((claim) => claim.id))
    }
  }, [claims, isModalOpen])

  const { mutate: applyClaims, isLoading: saving } = useMutation(saveEmployeeClaims, {
    onSuccess: () => {
      setModalOpen(false)
      queryClient.invalidateQueries('retrieveEmployeeData')
      queryClient.invalidateQueries(['employeeClaims', time_id, employee?.id])
    },
    onError: (error) => {
      Bugsnag.notify(error?.data || error)
      toast.error(error?.data?.message || 'Unable to save claims. Please try again.')
    },
  })

  const payableIds = claims.filter((claim) => !claim.paid).map((claim) => claim.id)
  const allSelected = payableIds.length > 0 && payableIds.every((id) => selectedIds.includes(id))
  const selectedClaims = claims.filter((claim) => selectedIds.includes(claim.id))
  const selectedCount = selectedClaims.filter((claim) => !claim.paid).length
  const total = selectedClaims.reduce((sum, claim) => sum + Number(claim.amount || 0), 0)

  const toggleClaim = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]))
  }

  const toggleAll = () => {
    setSelectedIds((prev) =>
      allSelected
        ? prev.filter((id) => !payableIds.includes(id))
        : [...new Set([...prev, ...payableIds])]
    )
  }

  return (
    <>
      <Box onClick={() => setModalOpen(true)}>{children}</Box>
      <AdxFormModal
        open={isModalOpen}
        onClose={() => setModalOpen(false)}
        handleClose={() => setModalOpen(false)}
        sxModal={{ width: 600, maxHeight: '90vh' }}
        childPadding={false}
        customHeader={
          <AdxActionButton
            sx={{ boxShadow: 'none', border: 'none', boxSizing: 'border-box', px: 3 }}
            title={employee?.name}
            description={`Claims to pay in ${period ?? 'this payroll'} (not taxed)`}
            icon={<Icon>receipt_long</Icon>}
            sxIcon={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            iconColor="alternate.main"
            iconBgColor="alternate.light"
          />
        }
        enableFooter
        actionText={selectedCount > 0 ? `Apply (${selectedCount})` : 'Apply'}
        isLoading={saving}
        actionButtonDisabled={saving || isLoading}
        handleAction={() =>
          applyClaims({ time_id, employee_id: employee?.id, claim_ids: selectedIds })
        }
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress color="alternate" size={40} />
          </Box>
        ) : claims.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 6 }}>
            <Icon sx={{ fontSize: 40, color: 'light-gray-text.main' }}>receipt_long</Icon>
            <Typography color="light-gray-text.main">
              No approved claims waiting for payment.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ px: 2, pt: 1 }}>
              <AdxFormControlLabel
                type="no-fill"
                control={<AdxCheckbox />}
                checked={allSelected}
                disabled={payableIds.length === 0}
                onChange={toggleAll}
                label={<Typography fontWeight={600}>{`Select all (${payableIds.length})`}</Typography>}
              />
            </Box>
            <Divider />
            <Box
              className="scrollbar"
              sx={{ display: 'flex', flexDirection: 'column', px: 2, py: 1, maxHeight: '45vh', overflow: 'auto' }}
            >
              {claims.map((claim) => (
                <AdxFormControlLabel
                  key={claim.id}
                  type="no-fill"
                  control={<AdxCheckbox />}
                  checked={selectedIds.includes(claim.id)}
                  disabled={claim.paid}
                  onChange={() => toggleClaim(claim.id)}
                  sx={{ mr: 0, '& .MuiFormControlLabel-label': { flex: 1 } }}
                  label={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Typography>{claim.title}</Typography>
                      <Typography>{`RM ${formatAmount(claim.amount)}`}</Typography>
                    </Box>
                  }
                  description={
                    claim.paid
                      ? 'Paid in this payroll'
                      : claim.approved_at && `Approved ${claim.approved_at}`
                  }
                />
              ))}
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 3,
                py: 2,
                bgcolor: 'gray-background.main',
                borderTop: 1,
                borderColor: 'gray-line.main',
              }}
            >
              <Typography color="light-gray-text.main">Total to pay</Typography>
              <Typography variant="h4" fontWeight={600}>{`RM ${formatAmount(total)}`}</Typography>
            </Box>
          </>
        )}
      </AdxFormModal>
    </>
  )
}

export default ClaimsModal
